import http from '../../utils/http';
import Translator from '../../utils/translation/Translator';
import namespaceTranslatedText from '../TranslatedText';
import startImpersonationActionCreator from '../../actioncreators/startImpersonation';
import { InlineMessage } from '../../components';
import UtilityHelper from '../../utils/helpers';
import { staticConstants } from '../../utils/constants';
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Modal, Button, Loader, ValidatedInput, Tooltip, TextArea } from '@idm/ui-components';

const buildErrorMessage = (error, fallback) => {
  const message = error.message || fallback;
  const { code } = error;
  const traceId =
    error.errors && error.errors[0] && error.errors[0].details && error.errors[0].details.traceId;
  const parts = [message];

  if (code && code !== 'UNKNOWN_ERROR') {
    parts.push(`(code: ${code})`);
  }
  if (traceId) {
    parts.push(`(trace: ${traceId})`);
  }
  return parts.join(' ');
};

const TranslatedText = namespaceTranslatedText('IMPERSONATION');
const { lifionHomePage } = staticConstants;

const I18nKeys = {
  modalTitle: 'MODAL_TITLE',
  selectedHeader: 'SELECTED_HEADER',
  associateLabel: 'ASSOCIATE_LABEL',
  clientInstanceLabel: 'CLIENT_INSTANCE_LABEL',
  confirmationHeader: 'CONFIRMATION_HEADER',
  reasonLabel: 'REASON_LABEL',
  reasonTooltip: 'REASON_TOOLTIP',
  reasonPlaceholder: 'REASON_PLACEHOLDER',
  jiraTicketLabel: 'JIRA_TICKET_LABEL',
  jiraTicketTooltip: 'JIRA_TICKET_TOOLTIP',
  jiraTicketPlaceholder: 'JIRA_TICKET_PLACEHOLDER',
  cancelButton: 'CANCEL_BUTTON',
  startButton: 'START_BUTTON',
  failureMessage: 'FAILURE_MESSAGE',
  mappingFailureMessage: 'MAPPING_FAILURE_MESSAGE',
  mappingNoIdError: 'MAPPING_NO_ID_ERROR',
};

const CommonI18nKeys = {
  requiredError: 'TEXT_INPUT.ERROR_MESSAGE',
  invalidUrlError: 'INVALID_URL_ERROR',
  noSpaceError: 'NO_SPACE_ERROR',
};

class ImpersonationModal extends React.PureComponent {
  static propTypes = {
    associateId: PropTypes.string.isRequired,
    clientId: PropTypes.string.isRequired,
    associateDisplayName: PropTypes.string.isRequired,
    clientName: PropTypes.string.isRequired,
    onCancel: PropTypes.func.isRequired,
    dictionary: PropTypes.object.isRequired,
    dispatchStart16FImpersonation: PropTypes.func.isRequired,
    dispatchResetState: PropTypes.func.isRequired,
  };

  impersonationDictionary = new Translator('IMPERSONATION', this.props.dictionary);

  constructor(props) {
    super(props);
    const { dictionary } = props;
    const translator = new Translator('IMPERSONATION', dictionary);
    const requiredTranslator = new Translator('', dictionary);
    const requiredFieldRule = new ValidatedInput.RequiredRule(
      requiredTranslator.get(CommonI18nKeys.requiredError)
    );
    const noSpaceError = translator.get(CommonI18nKeys.noSpaceError);

    this.reasonRules = [requiredFieldRule];
    this.jiraTicketRules = [
      requiredFieldRule,
      new ValidatedInput.UrlRule(translator.get(CommonI18nKeys.invalidUrlError)),
      new ValidatedInput.CustomRule((value) => {
        if (/\s/.test(value)) throw new Error(noSpaceError);
      }, noSpaceError),
    ];

    this.state = {
      reason: '',
      jiraTicket: '',
      isImpersonating: false,
      isReasonValid: false,
      isJiraTicketValid: false,
      isValidating: false,
      impersonationError: null,
    };
  }

  onStartImpersonateButtonClick = async () => {
    const { isReasonValid, isJiraTicketValid } = this.state;

    if (isReasonValid && isJiraTicketValid) {
      await this.startImpersonation();
    } else {
      this.setState({ isValidating: true });
    }
  };

  handleFieldChange = (event) => {
    const field = event.target.getAttribute('data-meta-id');
    const { value } = event.target;

    this.setState({ [field]: value });
  };

  handleReasonValidationChange = (validationResult) => {
    this.setState({ isReasonValid: validationResult.isValid ?? validationResult });
  };

  handleJiraTicketValidationChange = (validationResult) => {
    this.setState({ isJiraTicketValid: validationResult.isValid ?? validationResult });
  };

  startImpersonation = async () => {
    try {
      this.setState({ isImpersonating: true, impersonationError: null });

      const { reason, jiraTicket } = this.state;
      const { associateId, clientId, dispatchStart16FImpersonation, dictionary } = this.props;
      const translator = new Translator('IMPERSONATION', dictionary);

      let impersonationId;

      try {
        const { data } = await http.createImpersonationMapping(associateId);

        impersonationId = data.impersonationId;
      } catch (mappingError) {
        this.setState({
          impersonationError: buildErrorMessage(
            mappingError,
            translator.get(I18nKeys.mappingFailureMessage)
          ),
          isImpersonating: false,
        });
        return;
      }

      if (!impersonationId) {
        this.setState({
          impersonationError: translator.get(I18nKeys.mappingNoIdError),
          isImpersonating: false,
        });
        return;
      }

      const impersonationData = {
        kind: 'associate',
        impersonatedActorId: associateId,
        impersonationId,
        impersonatedClientId: clientId,
        reason,
        jiraTicket,
      };

      try {
        await dispatchStart16FImpersonation(impersonationData);
        UtilityHelper.redirectTo(lifionHomePage);
      } catch (impersonationError) {
        this.setState({
          impersonationError: buildErrorMessage(
            impersonationError,
            translator.get(I18nKeys.failureMessage)
          ),
          isImpersonating: false,
        });
      }
    } finally {
      this.setState({ isImpersonating: false });
      this.props.dispatchResetState();
    }
  };

  renderReasonFormField = () => {
    const { reason, isValidating } = this.state;
    const { dictionary } = this.props;
    const translator = new Translator('IMPERSONATION', dictionary);

    return (
      <div className="impersonate-form-fields">
        <div className="form-field-label">
          <span className="form-field-label-text">
            <TranslatedText textKey={I18nKeys.reasonLabel} />
          </span>
          <Tooltip message={translator.get(I18nKeys.reasonTooltip)} trigger="click" />
        </div>
        <ValidatedInput
          value={reason}
          dataMetaId="reason"
          onChange={this.handleFieldChange}
          handleValidationChange={this.handleReasonValidationChange}
          validateOnChange={true}
          forceValidationCheck={isValidating}
          placeholder={translator.get(I18nKeys.reasonPlaceholder)}
          InputComponent={TextArea}
          rules={this.reasonRules}
        />
      </div>
    );
  };

  renderJiraTicketFormField = () => {
    const { jiraTicket, isValidating } = this.state;
    const { dictionary } = this.props;
    const translator = new Translator('IMPERSONATION', dictionary);

    return (
      <div className="impersonate-form-fields">
        <div className="form-field-label">
          <span className="form-field-label-text">
            <TranslatedText textKey={I18nKeys.jiraTicketLabel} />
          </span>
          <Tooltip message={translator.get(I18nKeys.jiraTicketTooltip)} trigger="click" />
        </div>
        <ValidatedInput
          value={jiraTicket}
          dataMetaId="jiraTicket"
          onChange={this.handleFieldChange}
          handleValidationChange={this.handleJiraTicketValidationChange}
          validateOnChange={true}
          forceValidationCheck={isValidating}
          placeholder={translator.get(I18nKeys.jiraTicketPlaceholder)}
          InputComponent={TextArea}
          rules={this.jiraTicketRules}
        />
      </div>
    );
  };

  renderFooterButtons = () => {
    const { onCancel, dictionary } = this.props;
    const translator = new Translator('IMPERSONATION', dictionary);

    return [
      <Button
        buttonType="cancel"
        onClick={onCancel}
        key="cancel"
        value={translator.get(I18nKeys.cancelButton)}
        data-meta-id="cancel-button"
      />,
      <Button
        buttonType="primary"
        onClick={this.onStartImpersonateButtonClick}
        key="impersonate"
        value={translator.get(I18nKeys.startButton)}
        data-meta-id="impersonate-button"
      />,
    ];
  };

  renderLoader = () => {
    const { isImpersonating } = this.state;

    return isImpersonating ? <Loader className="data-loader" /> : null;
  };

  renderImpersonationInfo = () => {
    const { associateDisplayName, clientName } = this.props;

    return (
      <div className="info-container modal-body-container-margin">
        <p className="info-header-text">
          <TranslatedText textKey={I18nKeys.selectedHeader} />
        </p>
        <div className="info-container-body">
          <div className="section">
            <p>
              <TranslatedText textKey={I18nKeys.associateLabel} />
            </p>
            <span className="user-name-text">{associateDisplayName}</span>
          </div>
          <div className="section">
            <p>
              <TranslatedText textKey={I18nKeys.clientInstanceLabel} />
            </p>
            <span className="client-name-text">{clientName}</span>
          </div>
        </div>
      </div>
    );
  };

  renderImpersonateForm = () => {
    return (
      <div className="modal-body-container-margin">
        <p className="confirmation-header-text">
          <TranslatedText textKey={I18nKeys.confirmationHeader} />
        </p>
        <div className="impersonate-form">
          {this.renderReasonFormField()}
          {this.renderJiraTicketFormField()}
        </div>
      </div>
    );
  };

  renderError = () => {
    const { impersonationError } = this.state;

    if (!impersonationError) {
      return null;
    }

    return (
      <InlineMessage type="error" className="impersonation-error">
        {impersonationError}
      </InlineMessage>
    );
  };

  renderModalBody = () => {
    return (
      <div className="impersonation-modal-body">
        {this.renderLoader()}
        {this.renderError()}
        {this.renderImpersonationInfo()}
        {this.renderImpersonateForm()}
      </div>
    );
  };

  render() {
    const { onCancel, dictionary } = this.props;
    const translator = new Translator('IMPERSONATION', dictionary);
    const modalProps = {
      footer: this.renderFooterButtons(),
      isVisible: true,
      title: translator.get(I18nKeys.modalTitle),
      width: 450,
      onCancel,
    };

    return <Modal {...modalProps}>{this.renderModalBody()}</Modal>;
  }
}

function mapStateToProps(state) {
  return {
    dictionary: state.translation.main.dictionary,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    dispatchStart16FImpersonation(impersonationData) {
      return dispatch(startImpersonationActionCreator.start16FImpersonation(impersonationData));
    },
    dispatchResetState() {
      dispatch(startImpersonationActionCreator.resetState());
    },
  };
}

export { ImpersonationModal as UnwrappedImpersonationModal, mapStateToProps, mapDispatchToProps };
export default connect(mapStateToProps, mapDispatchToProps)(ImpersonationModal);
